import { createAsyncThunk } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { POST, wrapApiCall } from '@webapp/account/src/libs/request';
import {
    PROFILE_CHANGE_PASSWORD,
    PROFILE_CONFIRM_EMAIL,
    PROFILE_CONFIRM_RESET_PASSWORD,
    PROFILE_RESEND_CONFIRM_EMAIL,
} from '@webapp/account/src/resources/profile/constants';
import { initialLicense, mapLicense, mapQuotas } from '@webapp/account/src/resources/profile/license';
import { mapProfile, reverseMapProfile } from '@webapp/account/src/resources/profile/map';
import { initialProfile } from '@webapp/account/src/resources/profile/profile';
import { AccountRoutes } from '@webapp/account/src/resources/routes';

import { createFingerprint, getFingerprint } from '@webapp/common/lib/api';
import { setUserToken } from '@webapp/common/lib/cookies';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';
import { getUrlSearchParam, undefDestroy } from '@webapp/common/lib/utils';

export const authorize = createAsyncThunk(
    'profile/authorize',
    wrapApiCall<{
        email: string;
        password: string;
    }>(
        async ({ email, password }) => {
            const fingerprint = getFingerprint();
            const { confirm_type, device_confirmed, nikname, name, license, profile, restricted, userRole, token } =
                await POST(
                    'authorization',
                    {
                        user: email,
                        password,
                        fingerprint,
                    },
                    { withoutToken: true },
                );

            setUserToken(token);

            const profileData = {
                ...profile,
                userRole,
                restricted: !!restricted,
                device_confirmed,
                nikname,
                name,
                confirm_type,
            };
            const backUrl = LocalStorage.get<string>(LocalStorageKeys.BACK_URL);
            const url = getUrlSearchParam('backUrl', backUrl) || AccountRoutes.account.link;

            return {
                profile: {
                    ...cloneDeep(initialProfile),
                    ...(device_confirmed ? mapProfile(profileData) : profileData),
                },
                license: {
                    ...cloneDeep(initialLicense),
                    ...(device_confirmed ? mapLicense(license) : license),
                },
                url,
            };
        },
        { notify: false, notifyError: true },
    ),
);

export const getProfile = createAsyncThunk(
    'profile/getProfile',
    wrapApiCall<{
        auth: boolean;
    }>(
        async (_, { getState }) => {
            const resp = await POST('get/profile');

            const profileData = {
                ...(resp.profile?.[0] || {}),
                restricted: Boolean(resp.restricted),
                ...resp,
            };
            const prevLicence = getState().Profile.license;
            return {
                profile: {
                    ...cloneDeep(initialProfile),
                    ...mapProfile(profileData),
                },
                license: prevLicence.licenseId ? prevLicence : cloneDeep(initialLicense),
            };
        },
        { notify: false, notifyError: true },
    ),
);

export const getQuotas = createAsyncThunk(
    'profile/getQuotas',
    wrapApiCall(
        async () => {
            const resp = await POST('show/quotas');

            const license = {
                ...cloneDeep(initialLicense),
                ...mapLicense(resp.license),
                surveys: mapQuotas(resp.surveys),
            };

            return { license };
        },
        { notify: false, notifyError: true },
    ),
);

export const confirmDevice = createAsyncThunk(
    'profile/confirmDevice',
    wrapApiCall(async () => {
        const fingerprint = getFingerprint();

        const { confirm_type, message } = await POST('device/send', {
            fingerprint,
        });

        return {
            confirm_type,
            message,
        };
    }),
);

export const confirmDeviceHashCode = createAsyncThunk(
    'profile/confirmDeviceHash',
    wrapApiCall<{ code: string; hash: string }>(async ({ code, hash }) => {
        const { message } = await POST(
            'device/confirmation',
            undefDestroy({
                hash,
                code,
            }),
        );

        return { message };
    }),
);

export const register = createAsyncThunk(
    'profile/register',
    wrapApiCall<{ email: string; name: string; password: string }>(async ({ email, name, password }) => {
        const referer = localStorage.getItem('referrer') || undefined;
        const fingerprint = createFingerprint();

        const { token, message } = await POST(
            'registration',
            undefDestroy({
                user: email,
                fingerprint,
                password,
                name,
                referer,
            }),
        );

        setUserToken(token);

        return { url: AccountRoutes.account.link, message };
    }),
);

export const confirmEmail = createAsyncThunk(
    PROFILE_CONFIRM_EMAIL,
    wrapApiCall<{ token: string }>(async ({ token }) => {
        setUserToken(token);

        const fingerprint = getFingerprint();

        const { token: newToken } = await POST('confirmation', { fingerprint });

        setUserToken(newToken);

        return {};
    }),
);

export const resetPassword = createAsyncThunk(
    'profile/resetPassword',
    wrapApiCall<{ email: string }>(async ({ email }) => {
        const { message } = await POST('reset/password', { user: email });

        return { message };
    }),
);

export const saveProfile = createAsyncThunk(
    'profile/saveProfile',
    wrapApiCall<{ data: any; type: string }>(async ({ data }) => {
        const { message } = await POST('set/user_info', {
            data: JSON.stringify(reverseMapProfile(data)),
        });

        return { profile: data, message };
    }),
);

export const confirmResetPassword = createAsyncThunk(
    PROFILE_CONFIRM_RESET_PASSWORD,
    wrapApiCall<{ token: string }>(async ({ token }) => {
        setUserToken(token);
        const { token: newToken, message } = await POST('confirm/reset');
        setUserToken(newToken);

        return { message };
    }),
);

export const changePassword = createAsyncThunk(
    PROFILE_CHANGE_PASSWORD,
    wrapApiCall<{ password: string }>(async ({ password }) => {
        const { message } = await POST('change/password', { password });

        return { message };
    }),
);

export const resendConfirmEmail = createAsyncThunk(
    PROFILE_RESEND_CONFIRM_EMAIL,
    wrapApiCall(async () => {
        const { message } = await POST('resend');

        return { message };
    }),
);

export const changeEmailUnconfirmed = createAsyncThunk(
    'profile/changeEmailUnconfirmed',
    wrapApiCall<{ email: string }>(async ({ email }) => {
        const { token, message } = await POST('reset/email', { email });

        setUserToken(token);

        return { message };
    }),
);

export const createPayment = createAsyncThunk(
    'profile/createPayment',
    wrapApiCall<{ data: any }>(async (data) => {
        return POST('create/payment', data, { json: true });
    }),
);
